import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";

const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchAnnouncement();
  }, []);

  const fetchAnnouncement = async () => {
    try {
      // Set a short timeout to avoid blocking page load
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout
      
      const response = await axios.get(`${API}/settings/announcement`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      const data = response.data;
      
      if (data.enabled && data.message) {
        // Check if user dismissed this specific announcement
        const dismissedKey = `announcement_dismissed_${data.message.substring(0, 20)}`;
        const isDismissed = localStorage.getItem(dismissedKey);
        
        if (!isDismissed) {
          setAnnouncement(data);
        }
      }
    } catch (error) {
      // Silently fail - announcement is optional
      console.log("No announcement configured");
    }
  };

  const handleDismiss = () => {
    const dismissedKey = `announcement_dismissed_${announcement.message.substring(0, 20)}`;
    localStorage.setItem(dismissedKey, "true");
    setDismissed(true);
  };

  if (!announcement || dismissed) return null;

  const getBannerStyle = (type) => {
    const styles = {
      info: "bg-blue-500/10 border-blue-500/20 text-blue-400",
      warning: "bg-yellow-500/10 border-yellow-500/20 text-yellow-400",
      success: "bg-green-500/10 border-green-500/20 text-green-400",
      error: "bg-red-500/10 border-red-500/20 text-red-400",
    };
    return styles[type] || styles.info;
  };

  const getIcon = (type) => {
    const icons = {
      info: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      warning: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      success: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      error: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    };
    return icons[type] || icons.info;
  };

  return (
    <div className={`border-b ${getBannerStyle(announcement.type)}`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {getIcon(announcement.type)}
            <p className="text-sm font-medium">{announcement.message}</p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-current hover:opacity-70 transition shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
